import React, {useEffect} from "react";
import * as yup from "yup";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ITgBotForm} from "../../../store/tgBots/types";
import {TgBotsService} from "../../../services/tg_bots.service";
import {Button, Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row} from "reactstrap";
import NoSearchResult from "../../../components/Common/NoSearchResult";
import {useFormik} from "formik";
import LabelInput from "../../../components/Common/LabelInput";
import useOffices from "../../../hooks/useOffices";
import Select from "../../../components/Common/Select";
import {useDispatch, useSelector} from "react-redux";
import {IOffice} from "../../../store/offices/types";
import {clearOffices, getOffices} from "../../../store/offices/actions";

interface ModalTgBotProps {
    isOpen: boolean;
    tgBotId: number | null;
    onClose: () => void;
    onSuccess: () => void;
}

const ModalTgBot: React.FC<ModalTgBotProps> = ({isOpen, tgBotId, onSuccess, onClose}) => {
    const isEditModal = tgBotId !== null;
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    const {user_office_ids} = useOffices()
    const defaultOffice = user_office_ids.length === 1 ? user_office_ids : [];

    const offices = useSelector((state: any) => (state.Offices.data || []) as Array<IOffice>);
    const officesLoading = useSelector((state: any) => (state.Offices.loading || false) as boolean);

    const officeSelect =
        offices.map((item: any) => ({value: item.id, label: item.name}))

    useEffect(() => {
        dispatch(getOffices('', {}))

        return () => {
            dispatch(clearOffices())
        };
    }, []);

    const {
        data: tgBot,
        isFetching
    } = useQuery({
        queryKey: ["keyTgBot", tgBotId],
        queryFn: () => TgBotsService.getById(tgBotId || -1),
        enabled: isEditModal,
    });

    const {mutate: create, isPending: isPendingCreate} = useMutation({
        mutationFn: (data: ITgBotForm) => TgBotsService.create(data),
        onSuccess: onSuccess,
        mutationKey: ["keyCreateTgBot"],
    });

    const {mutate: update, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: ITgBotForm) => TgBotsService.update(tgBotId!, data),
        onSuccess: onSuccess,
        mutationKey: ["keyUpdateTgBot", tgBotId],
    });

    const validationSchema: yup.Schema<ITgBotForm> = yup.object().shape({
        name: yup.string()
            .min(1, "min 1 character")
            .required("Name is required"),

        pr_bot: yup.string()
            .min(1, "min 1 character")
            .required("PR Bot is required"),

        cl_bot: yup.string()
            .min(1, "min 1 character")
            .required("CL Bot is required"),

        error_bot: yup.string()
            .min(1, "min 1 character")
            .required("Error Bot is required"),

        pr_token: yup.string()
            .min(1, "min 1 character")
            .required("PR Token is required"),

        cl_token: yup.string()
            .min(1, "min 1 character")
            .required("CL Token is required"),

        error_token: yup.string()
            .min(1, "min 1 character")
            .required("Error Token is required"),

        office_ids: yup.array()
            .min(1, "Office is required")
            .required("Office is required"),
    });

    const initialValues: ITgBotForm = {
        name: tgBot?.name || "",
        pr_bot: tgBot?.pr_bot || "",
        cl_bot: tgBot?.cl_bot || "",
        error_bot: tgBot?.error_bot || "",
        pr_token: tgBot?.pr_token || "",
        cl_token: tgBot?.cl_token || "",
        error_token: tgBot?.error_token || "",
        office_ids: tgBot?.office_ids || defaultOffice
    }

    const handleSubmitForm = (result: ITgBotForm) => {
        if (!isEditModal) create(result);
        else update(result);
    };

    const {
        values,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
        errors,
        touched,
        isValid,
        resetForm,
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const handleChangeMulti = (name: string, value: any) => {
        const multiIds = value.map((item: any) => item.value)
        setFieldValue(name, multiIds)
    }

    const closeModal = () => {
        onClose();
        resetForm();
    };

    const isPendingSave = isPendingCreate || isPendingUpdate || !!queryClient.isFetching();

    return (
        <Modal isOpen={isOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal} tag="h4">
                {isEditModal ? "Edit" : "Add"} Telegram Bot
            </ModalHeader>
            {isFetching ? <div className="my-4"><NoSearchResult text="Loading..."/></div> : (
                <form onSubmit={handleSubmit} className="needs-validation">
                    <ModalBody>
                        <Row className="g-3">
                            <Col xs="12">
                                <LabelInput
                                    label="Name*"
                                    placeholder="Name*"
                                    name="name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.name}
                                    hasError={!!(errors.name && touched.name)}
                                    errorText={errors.name}
                                />
                            </Col>

                            <Col xs="12" sm="6">
                                <LabelInput
                                    label="PR Bot*"
                                    placeholder="PR Bot*"
                                    name="pr_bot"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.pr_bot}
                                    hasError={!!(errors.pr_bot && touched.pr_bot)}
                                    errorText={errors.pr_bot}
                                />
                            </Col>

                            <Col xs="12" sm="6">
                                <LabelInput
                                    label="PR Token*"
                                    placeholder="PR Token*"
                                    name="pr_token"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.pr_token}
                                    hasError={!!(errors.pr_token && touched.pr_token)}
                                    errorText={errors.pr_token}
                                />
                            </Col>

                            <Col xs="12" sm="6">
                                <LabelInput
                                    label="CL Bot*"
                                    placeholder="CL Bot*"
                                    name="cl_bot"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.cl_bot}
                                    hasError={!!(errors.cl_bot && touched.cl_bot)}
                                    errorText={errors.cl_bot}
                                />
                            </Col>

                            <Col xs="12" sm="6">
                                <LabelInput
                                    label="CL Token*"
                                    placeholder="CL Token*"
                                    name="cl_token"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.cl_token}
                                    hasError={!!(errors.cl_token && touched.cl_token)}
                                    errorText={errors.cl_token}
                                />
                            </Col>

                            <Col xs="12" sm="6">
                                <LabelInput
                                    label="Error Bot*"
                                    placeholder="Error Bot*"
                                    name="error_bot"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.error_bot}
                                    hasError={!!(errors.error_bot && touched.error_bot)}
                                    errorText={errors.error_bot}
                                />
                            </Col>

                            <Col xs="12" sm="6">
                                <LabelInput
                                    label="Error Token*"
                                    placeholder="Error Token*"
                                    name="error_token"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.error_token}
                                    hasError={!!(errors.error_token && touched.error_token)}
                                    errorText={errors.error_token}
                                />
                            </Col>

                            {!defaultOffice.length && (
                                <Col xs="12">
                                    <Label>Offices:</Label>
                                    <Select
                                        isMulti
                                        isSearchable
                                        label="Offices"
                                        placeholder="Offices"
                                        options={officeSelect}
                                        isLoading={officesLoading}
                                        disabled={officesLoading}
                                        onChange={(value: any) => handleChangeMulti("office_ids", value)}
                                        value={officeSelect.filter(item =>
                                            values?.office_ids.includes(item.value)
                                        )}
                                    />
                                </Col>
                            )}
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <div className="w-100 d-flex justify-content-between align-items-center flex-row-reverse">
                            <Button type="submit" color="success" disabled={!isValid || isPendingSave}>
                                {isPendingSave ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </ModalFooter>
                </form>)}
        </Modal>
    )
}

export default ModalTgBot;